import Papa from 'papaparse';
import type { ProductImage, SimpleItem } from '@/app/shared/types';
import { getSheetRows, isSheetsMode, SHEET_NAMES } from '@/app/shared/sheets';
import { locationTypes } from '@/app/shared/filters';
type Inventory = Record<string, unknown>;
type InventoryDetails = Record<string, unknown>;
type Location = Record<string, unknown>;
type ProductLocation = Record<string, unknown>;
type Sku = Record<string, unknown>;
const flatCsvModules = import.meta.glob('/src/mock-data/inventory-flat.csv', {
  as: 'raw',
  eager: true,
});
const flatCsv = (Object.values(flatCsvModules)[0] as string | undefined) ?? '';
export function hasInventoryFlatCsvData() {
  return Boolean(flatCsv.trim());
}
type FlatRow = {
  locationName: string;
  locationId: string;
  locationDescription: string | null;
  skuName: string;
  skuId: string;
  skuDescription: string | null;
  productName: string;
  productId: string;
  department: string | null;
  departmentId: string | null;
  category: string | null;
  brand: string | null;
  season: string | null;
  style: string | null;
  size: string | null;
  color: string | null;
  siteQty: number;
  transitQty: number;
  reservedQty: number;
  saleRate: number | null;
  coverage: number | null;
  price: number | null;
  cost: number | null;
  locationType: string | null;
  region: string | null;
  city: string | null;
  assortmentGroup: string | null;
  classification: string | null;
  market: string | null;
  atWh: number | null;
  optimalStock: number | null;
  minStock: number | null;
  maxStock: number | null;
  lastUpdate: string | null;
  creationDate: number;
  avoidReplenishment: boolean;
  locationAvoidReplenishment: boolean | null;
  skuAvoidReplenishment: boolean | null;
  masterSkuName: string | null;
  masterSkuExternalId: string | null;
  productLifeCycleStage: string | null;
  staplesStock: number | null;
  imageUrl: string | null;
  sourceLocationId: string | null;
  extraValues: Record<string, string | number | boolean | null>;
  extraLabels: Record<string, string>;
};
const SHEET_EXTRA_VALUES_KEY = 'sheet_extra_values';
const SHEET_EXTRA_LABELS_KEY = 'sheet_extra_labels';
const CLASSIFICATION_OPTIONS = ['A', 'B', 'C', 'D'];
function hashString(value: string) {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}
function getDerivedClassification(id: string) {
  return CLASSIFICATION_OPTIONS[hashString(id) % CLASSIFICATION_OPTIONS.length];
}
const CITY_OPTIONS = [
  'New York',
  'Los Angeles',
  'Chicago',
  'Houston',
  'Miami',
  'Seattle',
  'Denver',
  'Atlanta',
  'Boston',
  'San Francisco',
];
const REGION_OPTIONS = ['Northeast', 'West', 'Midwest', 'South', 'Southwest', 'Northwest'];
let loggedImageSamples = 0;
function getCityRegion(id: string) {
  const hash = hashString(id);
  return {
    city: CITY_OPTIONS[hash % CITY_OPTIONS.length],
    region: REGION_OPTIONS[hash % REGION_OPTIONS.length],
  };
}
function normalizeString(value: unknown) {
  if (value == null) {
    return null;
  }
  const text = String(value).trim();
  return text.length ? text : null;
}
function normalizeFieldKey(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}
function normalizeExtraFieldValue(value: unknown) {
  if (value == null) {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  const text = String(value).trim();
  if (!text) {
    return null;
  }

  const lowered = text.toLowerCase();
  if (['yes', 'true', '1', 'y'].includes(lowered)) {
    return true;
  }
  if (['no', 'false', '0', 'n'].includes(lowered)) {
    return false;
  }

  if (/^-?\$?\d[\d,]*(\.\d+)?%?$/.test(text)) {
    const numeric = extractNumber(text);
    if (numeric != null) {
      return numeric;
    }
  }

  return text;
}
function getImageFieldEntry(row: Record<string, unknown>) {
  const candidates = ['Image', 'Image URL', 'Image Url', 'Image Link', 'Image link', 'Image_URL'];
  for (const key of candidates) {
    if (Object.prototype.hasOwnProperty.call(row, key)) {
      const value = row[key];
      if (value != null && String(value).trim()) {
        return { key, value };
      }
    }
  }
  const keyMatch = Object.keys(row).find((key) => {
    const normalized = key.trim().toLowerCase();
    return (
      normalized === 'image' ||
      normalized === 'image url' ||
      normalized === 'image_url' ||
      normalized === 'image link' ||
      normalized === 'image_link'
    );
  });
  return keyMatch ? { key: keyMatch, value: row[keyMatch] } : null;
}
function extractNumber(value: unknown) {
  const text = String(value ?? '')
    .replace(/[$,%]/g, '')
    .replace(/,/g, '')
    .trim();
  if (!text) {
    return null;
  }
  const match = text.match(/-?\d+(\.\d+)?/);
  if (!match) {
    return null;
  }
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
}
function parseNumber(value: unknown, fallback = 0) {
  const parsed = extractNumber(value);
  return parsed ?? fallback;
}
function parseOptionalNumber(value: unknown) {
  return extractNumber(value);
}
function parseDateTimestamp(value: unknown) {
  if (value == null) {
    return 0;
  }
  const text = String(value).trim();
  if (!text) {
    return 0;
  }
  const parsed = Date.parse(text);
  return Number.isFinite(parsed) ? parsed : 0;
}
function parseBoolean(value: unknown, fallback = false) {
  if (value == null) {
    return fallback;
  }
  const text = String(value).trim().toLowerCase();
  if (!text) {
    return fallback;
  }
  if (['yes', 'true', '1', 'y'].includes(text)) {
    return true;
  }
  if (['no', 'false', '0', 'n'].includes(text)) {
    return false;
  }
  return fallback;
}
function parseOptionalBoolean(value: unknown) {
  if (value == null) {
    return null;
  }
  const text = String(value).trim().toLowerCase();
  if (!text) {
    return null;
  }
  if (['yes', 'true', '1', 'y'].includes(text)) {
    return true;
  }
  if (['no', 'false', '0', 'n'].includes(text)) {
    return false;
  }
  return null;
}
function normalizeImageUrl(url: string): string {
  const trimmed = url.trim();
  const imageFormula = trimmed.match(/^=IMAGE\((.+)\)$/i);
  if (imageFormula) {
    const rawArgs = imageFormula[1] ?? '';
    const firstArg = rawArgs.split(',')[0] ?? '';
    const stripped = firstArg.trim().replace(/^"|"$/g, '');
    return normalizeImageUrl(stripped);
  }
  if (!trimmed.includes('drive.google.com') && !trimmed.includes('googleusercontent.com')) {
    return trimmed;
  }
  const idMatch = trimmed.match(/\/d\/([a-zA-Z0-9_-]+)/);
  const idFromPath = idMatch ? idMatch[1] : null;
  if (idFromPath) {
    return `https://lh3.googleusercontent.com/d/${idFromPath}`;
  }
  try {
    const parsed = new URL(trimmed);
    const idFromQuery = parsed.searchParams.get('id');
    if (idFromQuery) {
      return `https://lh3.googleusercontent.com/d/${idFromQuery}`;
    }
  } catch (error) {
    void error;
  }
  return trimmed;
}
function extractDriveId(url: string) {
  const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
  if (match?.[1]) {
    return match[1];
  }
  try {
    const parsed = new URL(url);
    const idFromQuery = parsed.searchParams.get('id');
    if (idFromQuery) {
      return idFromQuery;
    }
  } catch (error) {
    void error;
  }
  return null;
}
function buildImages(url: string | null): ProductImage[] {
  if (!url) {
    return [];
  }
  const normalized = normalizeImageUrl(url);
  const domainMatch = normalized.match(/^https?:\/\/([^/]+)/);
  const domain = domainMatch ? domainMatch[1] : '';
  const images: ProductImage[] = [
    {
      public: normalized,
      thumb: normalized,
      domain,
    },
  ];
  const driveId = extractDriveId(url) ?? extractDriveId(normalized);
  if (driveId) {
    const fallback = `https://drive.google.com/uc?export=view&id=${driveId}`;
    if (fallback !== normalized) {
      images.push({
        public: fallback,
        thumb: fallback,
        domain: 'drive.google.com',
      });
    }
  }
  if (loggedImageSamples < 3 && typeof window !== 'undefined' && (import.meta as any)?.env?.DEV) {
    loggedImageSamples += 1;
    console.info('[demo images][inventory]', { raw: url, normalized, images });
  }
  return images;
}
function buildLocationType(raw: string | null) {
  if (!raw) {
    return 'store';
  }
  const normalized = raw.toLowerCase().trim();
  return locationTypes.includes(normalized) ? normalized : 'store';
}
function normalizeRow(row: Record<string, unknown>): FlatRow {
  const consumedKeys = new Set<string>();
  const readValue = (...keys: string[]) => {
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(row, key)) {
        consumedKeys.add(key);
        return row[key];
      }
    }
    return undefined;
  };
  const locationName = normalizeString(row.Location ?? row['Location Name']) ?? '';
  const locationId = normalizeString(row['Location ID'] ?? row.LocationId) ?? locationName;
  const locationDescription = normalizeString(
    readValue('Location Description', 'Location description', 'location_description'),
  );
  const skuName = normalizeString(row.SKU ?? row['SKU Name']) ?? '';
  const skuId = normalizeString(row['SKU ID'] ?? row.SkuId) ?? skuName;
  const skuDescription = normalizeString(
    row['SKU description'] ?? row['SKU Description'] ?? row.sku_description,
  );
  const productName = normalizeString(row.Product ?? row['Product Name']) ?? '';
  const productId = normalizeString(row['Product ID'] ?? row.ProductId) ?? productName;
  const region = normalizeString(row.Region);
  const city = normalizeString(row.City);
  consumedKeys.add('Location');
  consumedKeys.add('Location Name');
  consumedKeys.add('Location ID');
  consumedKeys.add('LocationId');
  consumedKeys.add('SKU');
  consumedKeys.add('SKU Name');
  consumedKeys.add('SKU ID');
  consumedKeys.add('SkuId');
  consumedKeys.add('SKU description');
  consumedKeys.add('SKU Description');
  consumedKeys.add('sku_description');
  consumedKeys.add('Product');
  consumedKeys.add('Product Name');
  consumedKeys.add('Product ID');
  consumedKeys.add('ProductId');
  consumedKeys.add('Region');
  consumedKeys.add('City');
  const imageField = getImageFieldEntry(row);
  if (imageField?.key) {
    consumedKeys.add(imageField.key);
  }
  const department = normalizeString(readValue('Department'));
  const departmentId = normalizeString(readValue('Department ID', 'department_id'));
  const category = normalizeString(readValue('Category'));
  const brand = normalizeString(readValue('Brand'));
  const season = normalizeString(readValue('Season'));
  const style = normalizeString(readValue('Style'));
  const size = normalizeString(readValue('Size'));
  const color = normalizeString(readValue('Color'));
  const siteQty = parseNumber(readValue('Site Qty', 'At site', 'At Site', 'site_qty', 'at_site'));
  const transitQty = parseNumber(
    readValue('Transit Qty', 'In transit', 'In Transit', 'transit_qty', 'in_transit'),
  );
  const reservedQty = parseNumber(
    readValue('Reserved Qty', 'Reserved', 'reserved_qty', 'reserved'),
  );
  const saleRate = parseOptionalNumber(
    readValue('Sale Rate', 'Sales rate', 'Sales Rate', 'sale_rate'),
  );
  const coverage = parseOptionalNumber(readValue('Coverage', 'coverage'));
  const price = parseOptionalNumber(readValue('Price'));
  const cost = parseOptionalNumber(readValue('Cost'));
  const locationType = normalizeString(
    readValue('Location Type', 'Location type', 'location_type'),
  );
  const assortmentGroup = normalizeString(readValue('Assortment Group', 'assortment_group'));
  const classification = normalizeString(readValue('Classification', 'classification'));
  const market = normalizeString(readValue('Market', 'market'));
  const atWh = parseOptionalNumber(readValue('At WH', 'at_wh'));
  const optimalStock = parseOptionalNumber(
    readValue('Optimal stock', 'Optimal Stock', 'optimal_stock'),
  );
  const minStock = parseOptionalNumber(
    readValue('Min stock', 'Min Stock', 'min_stock', 'min_target'),
  );
  const maxStock = parseOptionalNumber(
    readValue('Max stock', 'Max Stock', 'max_stock', 'max_target'),
  );
  const lastUpdate = normalizeString(
    readValue(
      'Last status date',
      'Last Status Date',
      'last_status_date',
      'Last update',
      'Last Update',
      'last_update',
    ),
  );
  const creationDate = parseDateTimestamp(
    readValue('Creation date', 'Creation Date', 'created_at'),
  );
  const avoidReplenishment = parseBoolean(readValue('Avoid replenishment', 'avoid_replenishment'));
  const locationAvoidReplenishment = parseOptionalBoolean(
    readValue(
      'Location Avoid Replenishment',
      'Location avoid replenishment',
      'Avoid Replenishment For Location',
      'location_avoid_replenishment',
      'avoid_replenishment_for_location',
    ),
  );
  const skuAvoidReplenishment = parseOptionalBoolean(
    readValue(
      'SKU Avoid Replenishment',
      'Sku Avoid Replenishment',
      'Avoid Replenishment For SKU',
      'sku_avoid_replenishment',
      'avoid_replenishment_for_sku',
    ),
  );
  const masterSkuName = normalizeString(
    readValue('Master SKU Name', 'Master Sku Name', 'master_sku_name'),
  );
  const masterSkuExternalId = normalizeString(
    readValue(
      'Master SKU External ID',
      'Master SKU ID',
      'Master Sku External ID',
      'master_sku_external_id',
      'master_sku_id',
    ),
  );
  const productLifeCycleStage = normalizeString(
    readValue(
      'Product Life Cycle Stage',
      'Product Lifecycle Stage',
      'Product lifecycle stage',
      'Lifecycle Stage',
      'Lifecycle',
      'product_life_cycle_stage',
    ),
  );
  const staplesStock = parseOptionalNumber(
    readValue('Staples Stock', 'Staple Stock', 'staples_stock'),
  );
  const sourceLocationId = normalizeString(
    readValue(
      'Source Location External ID',
      'Source Location ID',
      'WH name',
      'WH Name',
      'Source',
      'source_location_external_id',
      'source_location_id',
    ),
  );
  const extraValues: Record<string, string | number | boolean | null> = {};
  const extraLabels: Record<string, string> = {};
  Object.entries(row).forEach(([key, value]) => {
    if (consumedKeys.has(key)) {
      return;
    }
    const normalizedKey = normalizeFieldKey(key);
    if (!normalizedKey) {
      return;
    }
    const normalizedValue = normalizeExtraFieldValue(value);
    if (normalizedValue == null) {
      return;
    }
    if (!Object.prototype.hasOwnProperty.call(extraValues, normalizedKey)) {
      extraValues[normalizedKey] = normalizedValue;
      extraLabels[normalizedKey] = key;
    }
  });
  return {
    locationName,
    locationId,
    locationDescription,
    skuName,
    skuId,
    skuDescription,
    productName,
    productId,
    department,
    departmentId,
    category,
    brand,
    season,
    style,
    size,
    color,
    siteQty,
    transitQty,
    reservedQty,
    saleRate,
    coverage,
    price,
    cost,
    locationType,
    region,
    city,
    assortmentGroup,
    classification,
    market,
    atWh,
    optimalStock,
    minStock,
    maxStock,
    lastUpdate,
    creationDate,
    avoidReplenishment,
    locationAvoidReplenishment,
    skuAvoidReplenishment,
    masterSkuName,
    masterSkuExternalId,
    productLifeCycleStage,
    staplesStock,
    imageUrl: normalizeString(imageField?.value),
    sourceLocationId,
    extraValues,
    extraLabels,
  };
}
type InventoryFlatSheetsContext = 'inventory' | 'rules-engine';

async function loadFlatRows(context: InventoryFlatSheetsContext = 'inventory'): Promise<FlatRow[]> {
  if (isSheetsMode(context)) {
    const rows = await getSheetRows<Record<string, unknown>>(
      SHEET_NAMES.inventoryFlat,
      context,
    );
    return rows.map((row) => normalizeRow(row));
  }
  if (!flatCsv.trim()) {
    return [];
  }
  const parsed = Papa.parse<Record<string, unknown>>(flatCsv, {
    header: true,
    skipEmptyLines: true,
  });
  return parsed.data.map((row) => normalizeRow(row));
}
function toSimpleItems(values: string[]) {
  return values.map((value) => ({ id: value, name: value }) satisfies SimpleItem);
}
export async function getInventoryOptionsFromFlat() {
  const rows = await loadFlatRows();
  const locations = new Map<string, string>();
  const skus = new Map<string, string>();
  const products = new Map<string, string>();
  const brands = new Set<string>();
  const seasons = new Set<string>();
  const styles = new Set<string>();
  const categories = new Set<string>();
  const departments = new Set<string>();
  const regions = new Set<string>();
  const cities = new Set<string>();
  const colors = new Set<string>();
  const sourceLocations = new Set<string>();
  const classifications = new Set<string>();
  const assortmentGroups = new Set<string>();
  rows.forEach((row) => {
    if (row.locationId) {
      locations.set(row.locationId, row.locationName || row.locationId);
    }
    if (row.skuId) {
      skus.set(row.skuId, row.skuName || row.skuId);
    }
    if (row.productId) {
      products.set(row.productId, row.productName || row.productId);
    }
    if (row.brand) {
      brands.add(row.brand);
    }
    if (row.season) {
      seasons.add(row.season);
    }
    if (row.style) {
      styles.add(row.style);
    }
    if (row.category) {
      categories.add(row.category);
    }
    if (row.color) {
      colors.add(row.color);
    }
    if (row.sourceLocationId) {
      sourceLocations.add(row.sourceLocationId);
    }
    if (row.department) {
      departments.add(row.department);
    }
    if (row.assortmentGroup) {
      assortmentGroups.add(row.assortmentGroup);
    }
    const city = row.city ?? getCityRegion(row.locationId).city;
    const region = row.region ?? getCityRegion(row.locationId).region;
    cities.add(city);
    regions.add(region);
    const classification = row.classification ?? getDerivedClassification(row.locationId);
    classifications.add(classification);
  });
  return {
    locations: Array.from(locations.entries()).map(([id, name]) => ({
      id,
      name,
      external_id: id,
    })),
    skus: Array.from(skus.entries()).map(([id, name]) => ({
      id,
      name,
      external_id: id,
    })),
    products: Array.from(products.entries()).map(([id, name]) => ({
      id,
      name,
      external_id: id,
    })),
    brands: toSimpleItems(Array.from(brands)),
    seasons: toSimpleItems(Array.from(seasons)),
    styles: toSimpleItems(Array.from(styles)),
    categories: toSimpleItems(Array.from(categories)),
    colors: toSimpleItems(Array.from(colors)),
    departments: toSimpleItems(Array.from(departments)),
    regions: toSimpleItems(Array.from(regions)),
    cities: toSimpleItems(Array.from(cities)),
    classifications: toSimpleItems(Array.from(classifications)),
    assortmentGroups: toSimpleItems(Array.from(assortmentGroups)),
    sourceLocations: toSimpleItems(Array.from(sourceLocations)),
  };
}
export async function getInventoryLocationsFromFlat(): Promise<Location[]> {
  const rows = await loadFlatRows();
  const byLocation = new Map<
    string,
    {
      row: FlatRow;
      siteQty: number;
      transitQty: number;
      reservedQty: number;
      inventoryCost: number;
      inventoryPrice: number;
      products: Set<string>;
      skus: Set<string>;
      brands: Set<string>;
      classifications: Set<string>;
      avoidReplenishment: boolean;
    }
  >();
  rows.forEach((row) => {
    const existing = byLocation.get(row.locationId);
    if (!existing) {
      byLocation.set(row.locationId, {
        row,
        siteQty: row.siteQty,
        transitQty: row.transitQty,
        reservedQty: row.reservedQty,
        inventoryCost: (row.cost ?? 0) * row.siteQty,
        inventoryPrice: (row.price ?? 0) * row.siteQty,
        products: new Set(row.productId ? [row.productId] : []),
        skus: new Set(row.skuId ? [row.skuId] : []),
        brands: new Set(row.brand ? [row.brand] : []),
        classifications: new Set(row.classification ? [row.classification] : []),
        avoidReplenishment: row.avoidReplenishment,
      });
      return;
    }
    existing.siteQty += row.siteQty;
    existing.transitQty += row.transitQty;
    existing.reservedQty += row.reservedQty;
    existing.inventoryCost += (row.cost ?? 0) * row.siteQty;
    existing.inventoryPrice += (row.price ?? 0) * row.siteQty;
    if (row.productId) {
      existing.products.add(row.productId);
    }
    if (row.skuId) {
      existing.skus.add(row.skuId);
    }
    if (row.brand) {
      existing.brands.add(row.brand);
    }
    if (row.classification) {
      existing.classifications.add(row.classification);
    }
    if (row.avoidReplenishment) {
      existing.avoidReplenishment = true;
    }
  });
  return Array.from(byLocation.values()).map((entry) => {
    const { row } = entry;
    const { city, region } =
      row.region && row.city
        ? { city: row.city, region: row.region }
        : getCityRegion(row.locationId);
    return {
      id: row.locationId,
      name: row.locationName,
      account_id: 'demo-account',
      at_site: entry.siteQty,
      avoid_replenishment: entry.avoidReplenishment,
      brands: Array.from(entry.brands),
      city,
      classifications: entry.classifications.size
        ? Array.from(entry.classifications)
        : [getDerivedClassification(row.locationId)],
      created_at: 0,
      deleted_at: 0,
      description: row.locationDescription,
      external_id: row.locationId,
      in_transit: entry.transitQty,
      inventory_cost: entry.inventoryCost,
      inventory_price: entry.inventoryPrice,
      location_type: buildLocationType(row.locationType),
      num_of_products: entry.products.size,
      num_of_skus: entry.skus.size,
      region,
      reserved: entry.reservedQty,
      updated_at: 0,
      custom_data: {},
    } satisfies Location;
  });
}
export async function getInventorySkusFromFlat(): Promise<Sku[]> {
  const rows = await loadFlatRows();
  const bySku = new Map<
    string,
    {
      row: FlatRow;
      siteQty: number;
      transitQty: number;
      reservedQty: number;
      locations: Set<string>;
      brands: Set<string>;
      categories: Set<string>;
      colors: Set<string>;
      seasons: Set<string>;
      styles: Set<string>;
      avoidReplenishment: boolean;
    }
  >();
  rows.forEach((row) => {
    const existing = bySku.get(row.skuId);
    if (!existing) {
      bySku.set(row.skuId, {
        row,
        siteQty: row.siteQty,
        transitQty: row.transitQty,
        reservedQty: row.reservedQty,
        locations: new Set(row.locationId ? [row.locationId] : []),
        brands: new Set(row.brand ? [row.brand] : []),
        categories: new Set(row.category ? [row.category] : []),
        colors: new Set(row.color ? [row.color] : []),
        seasons: new Set(row.season ? [row.season] : []),
        styles: new Set(row.style ? [row.style] : []),
        avoidReplenishment: row.avoidReplenishment,
      });
      return;
    }
    existing.siteQty += row.siteQty;
    existing.transitQty += row.transitQty;
    existing.reservedQty += row.reservedQty;
    if (row.locationId) {
      existing.locations.add(row.locationId);
    }
    if (row.brand) {
      existing.brands.add(row.brand);
    }
    if (row.category) {
      existing.categories.add(row.category);
    }
    if (row.color) {
      existing.colors.add(row.color);
    }
    if (row.season) {
      existing.seasons.add(row.season);
    }
    if (row.style) {
      existing.styles.add(row.style);
    }
    if (row.avoidReplenishment) {
      existing.avoidReplenishment = true;
    }
  });
  return Array.from(bySku.values()).map((entry) => {
    const { row } = entry;
    return {
      id: row.skuId,
      name: row.skuName,
      account_id: 'demo-account',
      avoid_replenishment: entry.avoidReplenishment,
      brands: Array.from(entry.brands),
      categories: Array.from(entry.categories),
      colors: Array.from(entry.colors),
      cost: row.cost,
      coverage: row.coverage,
      created_at: row.creationDate,
      deleted_at: 0,
      description: row.skuDescription,
      external_id: row.skuId,
      markets: row.market ? [row.market] : [],
      pack_constraint: null,
      price: row.price,
      product_description: row.productName,
      product_id: row.productId,
      product_external_id: row.productId,
      product_name: row.productName,
      reserved_qty: entry.reservedQty,
      sale_rate: row.saleRate,
      seasons: Array.from(entry.seasons),
      site_qty: entry.siteQty,
      size: row.size,
      styles: Array.from(entry.styles),
      total_stores: entry.locations.size,
      transit_qty: entry.transitQty,
      updated_at: 0,
      wh_qty: null,
      department_id: row.departmentId ?? row.department ?? null,
      department_name: row.department,
      department_external_id: row.departmentId ?? row.department ?? '',
      custom_data: {},
      master_sku_external_id: row.masterSkuExternalId,
      master_sku_name: row.masterSkuName,
    } satisfies Sku;
  });
}
function buildInventoryFromFlatRow(row: FlatRow): Inventory {
  const { city, region } =
    row.region && row.city
      ? { city: row.city, region: row.region }
      : getCityRegion(row.locationId);
  const locationAvoidReplenishment = row.locationAvoidReplenishment ?? row.avoidReplenishment;
  const skuAvoidReplenishment = row.skuAvoidReplenishment ?? row.avoidReplenishment;
  const avoidReplenishment =
    row.avoidReplenishment || locationAvoidReplenishment || skuAvoidReplenishment;

  return {
    id: `${row.locationId}:${row.skuId}`,
    location_id: row.locationId,
    location_external_id: row.locationId,
    location_name: row.locationName,
    location_avoid_replenishment: locationAvoidReplenishment,
    sku_id: row.skuId,
    sku_name: row.skuName,
    sku_external_id: row.skuId,
    sku_avoid_replenishment: skuAvoidReplenishment,
    sku_description: row.skuDescription,
    account_id: 'demo-account',
    avoid_replenishment: avoidReplenishment,
    brands: row.brand ? [row.brand] : [],
    categories: row.category ? [row.category] : [],
    city_name: city,
    classifications: row.classification
      ? [row.classification]
      : [getDerivedClassification(row.locationId)],
    colors: row.color ? [row.color] : [],
    cost: row.cost,
    coverage: row.coverage,
    created_at: row.creationDate,
    deleted_at: 0,
    location_type: buildLocationType(row.locationType),
    markets: row.market ? [row.market] : [],
    price: row.price,
    product_description: row.productName,
    product_id: row.productId,
    product_external_id: row.productId,
    product_name: row.productName,
    region_name: region,
    replenishment_time: null,
    reserved_qty: row.reservedQty,
    sale_rate: row.saleRate,
    location_description: row.locationDescription,
    seasons: row.season ? [row.season] : [],
    site_qty: row.siteQty,
    size: row.size ?? '',
    source_locations: row.sourceLocationId ? [row.sourceLocationId] : [],
    source_location_external_id: row.sourceLocationId ?? '',
    source_location_type: 'warehouse',
    styles: row.style ? [row.style] : [],
    transit_qty: row.transitQty,
    at_wh: row.atWh,
    updated_at: 0,
    department_id: row.departmentId ?? row.department ?? null,
    department_name: row.department,
    department_external_id: row.departmentId ?? row.department ?? '',
    assortment_group_name: row.assortmentGroup,
    optimal_stock: row.optimalStock,
    min_target: row.minStock,
    max_target: row.maxStock,
    image_url: row.imageUrl,
    custom_data: {
      [SHEET_EXTRA_VALUES_KEY]: row.extraValues,
      [SHEET_EXTRA_LABELS_KEY]: row.extraLabels,
    },
    last_status_date: row.lastUpdate,
    master_optimal_stock: null,
    master_sku_external_id: row.masterSkuExternalId,
    master_sku_name: row.masterSkuName,
    product_life_cycle_stage: row.productLifeCycleStage,
    staples_stock: row.staplesStock,
  } satisfies Inventory;
}

export async function getInventoryInventoriesFromFlat(): Promise<Inventory[]> {
  const rows = await loadFlatRows();
  return rows.map((row) => buildInventoryFromFlatRow(row));
}

export async function getRulesEngineScopeInventoriesFromFlat(): Promise<Inventory[]> {
  const rows = await loadFlatRows('rules-engine');
  return rows.map((row) => buildInventoryFromFlatRow(row));
}
export async function getInventoryProductLocationsFromFlat(): Promise<ProductLocation[]> {
  const rows = await loadFlatRows();
  const byProductLocation = new Map<string, FlatRow>();
  rows.forEach((row) => {
    const key = `${row.productId}:${row.locationId}`;
    if (!byProductLocation.has(key)) {
      byProductLocation.set(key, row);
    }
  });
  return Array.from(byProductLocation.entries()).map(([key, row]) => ({
    id: key,
    is_store_transfer_enabled: true,
    location_city_name: row.city ?? getCityRegion(row.locationId).city,
    location_cluster_name: row.region ?? getCityRegion(row.locationId).region,
    location_external_id: row.locationId,
    location_id: row.locationId,
    location_name: row.locationName,
    product_assortment_group_name: row.assortmentGroup ?? '',
    product_brand_names: row.brand ? [row.brand] : [],
    product_category_names: row.category ? [row.category] : [],
    product_department_names: row.department ? [row.department] : [],
    product_external_id: row.productId,
    product_id: row.productId,
    product_name: row.productName,
    product_price: row.price,
    product_season_names: row.season ? [row.season] : [],
    sale_rate: row.saleRate,
    product_global_sale_rate: row.saleRate,
  }));
}
export async function getInventoryDetailsFromFlat(id: string): Promise<InventoryDetails | null> {
  const rows = await loadFlatRows();
  const row = rows.find((item) => `${item.locationId}:${item.skuId}` === id);
  if (!row) {
    return null;
  }
  return {
    id,
    sku_external_id: row.skuId,
    size: row.size ?? '',
    replenishment: 0,
    sale_rate: row.saleRate,
    site_qty: row.siteQty,
    transit_qty: row.transitQty,
    optimal_stock: row.optimalStock ?? row.siteQty,
    sold: 0,
    wh_names: row.sourceLocationId ? [row.sourceLocationId] : [],
    next_replenishment: '',
    wh_qty: 0,
    coverage: row.coverage,
    expected_coverage: row.coverage,
    constraints: [],
    last_in_on: null,
    master_sku_external_id: row.masterSkuExternalId,
    master_sku_name: row.masterSkuName,
  } satisfies InventoryDetails;
}
export async function getInventoryProductImagesFromFlat(): Promise<Map<string, ProductImage[]>> {
  const rows = await loadFlatRows();
  const images = new Map<string, ProductImage[]>();
  rows.forEach((row) => {
    if (!images.has(row.productId) && row.imageUrl) {
      images.set(row.productId, buildImages(row.imageUrl));
    }
  });
  return images;
}
